import type { Dispatch } from "react";
import type { Order } from "../../lib/types.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import {
  bestellungAendern,
  NachladenNachSchreibenFehler,
  SSE_ENTITAET as BESTELLUNG_ENTITAET,
} from "../../api/bestellungen.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import { bestellbezeichnung } from "../../lib/bestellkennung.ts";

/**
 * Generischer Feldgruppen-Schreibweg fuer Bestelldaten im Drawer. Fehler gehen seit der Fehleranzeige
 * (Paket 1) in den Fehlerstapel; einen Toast gibt es hier nicht.
 */
export function useDrawerBestellfelder(
  bestellungen: readonly Order[],
  versende: Dispatch<Aktion>,
) {
  /**
   * `patch(key, value)` und seine Verwandten (`patchEta`, `setContainerType`, `patchCalc`, `patchPayment`,
   * `setTaskPhaseDone`, `docsManual` …) als REST-Schreibvorgang statt lokaler Mutation + `save()` - der
   * Bestand haelt seinen gesamten Datenbestand im Speicher und schreibt bei jedem Tastendruck lokal
   * durch, die SPA schreibt stattdessen gegen den Server. EIN gemeinsamer Kanal fuer ALLE fuenf
   * Reiter (Task 5, `MainTab/typen.ts#BestellungTabProps.onAendern` und wortgleich in
   * `TasksTabProps`/`DokumenteTabProps`/`ZahlungTabProps`/`EkTabProps`): jeder Reiter liefert bereits
   * das fertige Aenderungsobjekt, hier wird nur noch nachgeschlagen, geschrieben und gemeldet -
   * dieselbe Form wie `boardGruppeAendern` in `App.tsx`. `drawerFeldAendern` (Task 4, `onFeld` der beiden
   * Kopffelder) ist seit hier ein duenner Aufruf DIESER Funktion, keine zweite Kopie der Logik.
   *
   * `onErfolg` (Nachtrag "Archivieren verdrahten", Pruefrunde 2026-08-27): optional, NUR fuer
   * `drawerArchivUmschalten` in `App.tsx` - die bestehenden Aufrufer (`drawerFeldAendern`,
   * `boardZelleAendern`; `drawerBelegLoeschen` entfiel mit Ticket H) lassen ihn weg und verhalten sich
   * unveraendert.
   * Laeuft NACH `versende(...)`, also NACH dem Uebernehmen des Serverstands in den Speicher - fuer
   * den Archiv-Fall wichtig, weil der dortige Toast/`drawerSchliessen()` erst greifen soll, wenn der
   * neue `archived`-Wert tatsaechlich im Speicher steht (dieselbe Reihenfolge wie `drawerLoeschen`
   * in `App.tsx`: erst `versende`, dann `drawerSchliessen()`).
   *
   * `onFehler` (Nachbesserung "Zahlung anstossen sequenziert", Asana 1218171668075282): optional,
   * NUR fuer `drawerZahlungAnstossen` in `useDrawerTasks.ts` - laeuft im `.catch` unten, NACH der bestehenden
   * Protokollierung/Sammelmeldung, nie zusaetzlich zu `onErfolg`. Zweck: eine Klicksperre
   * freigeben, die sonst dauerhaft haengen bliebe, wenn der Extras-Schritt scheitert - die uebrigen
   * Aufrufer lassen ihn weg und verhalten sich unveraendert (`drawerBelegLoeschen`, einer von ihnen, entfiel mit
   * Ticket H). Er bekommt den Fehler (R-T6c, Fehleranzeige
   * Paket 1): ein `NachladenNachSchreibenFehler` heisst "Extras geschrieben, nur das Nachladen scheiterte" -
   * `onErfolg` laeuft dann nie, und der Aufrufer muss dem Nutzer sagen koennen, was deshalb NICHT geschah.
   * Beim verworfenen Erfolg eines weitergezogenen Drawers (`.then` unten) gibt es keinen Fehler: `onFehler`
   * kommt dann OHNE Argument (R-T6g, auch hier geht `onErfolg` nie, der Aufrufer muss es melden koennen).
   *
   * `hintergrund`: NUR fuer den automatischen Archivierungs-Sweep (`App.tsx`, Wrapper um diese
   * Funktion fuer `useAutoArchivierung`). Eine automatische Archivierung ist nie eine Handlung des
   * Menschen, die ganze Kette (PATCH und beide GETs des Nachladens) geht als Hintergrundanfrage hinaus.
   * Nur bei `true` an `bestellungAendern` weitergereicht: alle anderen Aufrufe behalten ihre drei
   * Argumente (Eingabe des Menschen im Drawer).
   *
   * FEHLER (Fehleranzeige Paket 1): gehen in den Fehlerstapel, der feste Satz ist der `kontext`. Ein Fehler
   * wird NIE verworfen (R3) - auch nicht, wenn `istAktuell` den Drawer als weitergezogen meldet; dann (und
   * beim Hintergrund-Sweep, den kein Mensch ausgeloest hat) nennt der Kontext die Bestellung. Nur ein Erfolg
   * eines nicht mehr offenen Drawers darf verworfen werden. Ein Abbruch (`AbortError`) und ein 401 melden
   * nichts, das entscheidet `meldeFehler`. Ein `NachladenNachSchreibenFehler` heisst: geschrieben, nur das
   * Nachladen scheiterte (R4) - kein Fehlschlag der Aenderung.
   */
  function drawerAendern(
    id: string,
    aenderung: Record<string, unknown>,
    onErfolg?: () => void,
    onFehler?: (fehler?: unknown) => void,
    // Zahlungsauftrag: Extras und Aufgabe teilen den Guard fuer Drawer-Wechsel/Unmount.
    istAktuell?: () => boolean,
    hintergrund?: boolean,
  ): void {
    const bestellung = bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    // BEFUND (task-rowversion-befunde.md): `bestellungAendern` schreibt ueber `PATCH
    // /api/orders/:id/<gruppe>` gegen `order_extras.row_version` (backend/src/domaene/extras.ts:64-70,
    // 129-135), NICHT gegen `orders.row_version` - anders als `drawerLoeschen` in `App.tsx`, das wirklich die
    // Kopfversion prueft. `entschachtele()` (api/bestellungen.ts) fuehrt die Extras-Version deshalb
    // unter einem EIGENEN Namen, `extrasRowVersion`. Bis zu diesem Task schickte diese Stelle
    // `bestellung.rowVersion` (die Kopfversion) - beide Zaehler starten bei 1, also gelang nur der
    // ERSTE Schreibvorgang je Bestellung zufaellig; jeder weitere lief deterministisch in einen 409,
    // weil sich seither nur noch `order_extras.row_version` erhoeht.
    //
    // RUECKFALL, wenn `extrasRowVersion` fehlt (eine Bestellung ohne `order_extras`-Zeile, `extras:
    // null` in der GET-Antwort - siehe `entschachtele()`): bewusst `0`, nicht `undefined`/`NaN`. Jede
    // Migration setzt `order_extras.row_version integer NOT NULL DEFAULT 1`
    // (backend/migrations/001_fundament.sql:63 und weitere) - eine ECHTE Zeile traegt also NIE `0`.
    // `0` kann folglich NIE zufaellig auf die echte Version treffen: fehlt die Zeile wirklich,
    // antwortet der Server mit einem sauberen 404 ("Kein Datensatz ... in order_extras gefunden",
    // `schreibeExtras`/`updateVersioned`); besteht sie entgegen der Annahme doch (ein veralteter
    // Store-Eintrag), antwortet er mit 409 samt dem aktuellen Stand. Beides ist ein lauter,
    // diagnostizierbarer Fehlschlag, nie ein stilles Ueberschreiben - anders als der Befund, der
    // diesen Task ausgeloest hat, verschluckt dieses `|| 0` also keine Unterscheidung: kein Wert waere
    // hier faelschlich erfolgreich.
    const extrasRowVersion = Number((bestellung as { extrasRowVersion?: unknown }).extrasRowVersion) || 0;
    (hintergrund
      ? bestellungAendern(id, aenderung, extrasRowVersion, true)
      : bestellungAendern(id, aenderung, extrasRowVersion)
    )
      .then((neu) => {
        if (istAktuell && !istAktuell()) { onFehler?.(); return; }
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
        onErfolg?.();
      })
      .catch((fehler: unknown) => {
        const drawerWeitergezogen = istAktuell !== undefined && !istAktuell();
        // Task 5a: der Fehler selbst wird PROTOKOLLIERT, nicht nur die Sammelmeldung gezeigt.
        // `bestellungAendern` weist seit Task 5a ein Feld ohne Feldgruppe ab und nennt in der
        // Meldung den richtigen Weg (`FeldOhneGruppeFehler`, api/bestellungen.ts). Ohne diese Zeile
        // verschluckt das `.catch` genau die Diagnose, um derentwillen der Riegel gebaut wurde - die
        // Sammelmeldung sagt dem Entwickler nicht, WELCHES Feld keinen Endpunkt hat. Die Meldung an
        // den Nutzer bleibt unveraendert.
        console.error(`Bestellung "${id}" konnte nicht geaendert werden:`, fehler);
        // Die Bestellung beim Namen nennen, wenn der Nutzer sie nicht vor Augen hat (R3, Hintergrund-Sweep).
        const bestellungText =
          drawerWeitergezogen || hintergrund
            ? `Die Bestellung ${bestellbezeichnung(bestellung, id)}`
            : "Die Bestellung";
        if (fehler instanceof NachladenNachSchreibenFehler) {
          // R-Z2: kein Wiederholungsrat der Ursache hinter dem Erfolgssatz. `fehler.ursache` statt der Huelle: aus
          // einem `NachladenNachSchreibenFehler` machte `fehlertext` den Sammeltext "Unerwarteter Fehler".
          meldeNachladeFehler(
            fehler.ursache,
            `${bestellungText} ist gespeichert, die Anzeige konnte aber nicht aktualisiert werden. Bitte neu laden.`,
          );
        } else {
          meldeFehler(
            fehler,
            hintergrund
              ? `${bestellungText} konnte nicht automatisch archiviert werden.`
              : `${bestellungText} konnte nicht geändert werden.`,
          );
        }
        onFehler?.(fehler);
      });
  }

  /**
   * `onFeld` der beiden Kopffelder (`DrawerKopf.tsx`, Task 4) - siehe `drawerAendern` oben.
   *
   * DIE BEIDEN KOPFFELDER SIND NICHT GLEICHWERTIG, und das stand bis zum Gesamtreview 2026-08-11
   * (Important 3) NIRGENDS - anders als bei `tasks` in `useDrawerTasks.ts`, wo dieselbe Klasse vorbildlich
   * benannt ist:
   *
   *   `name`     GEHT DURCH. `name` steht in `FELDGRUPPEN_SPALTEN.extras` (`api/bestellungen.ts`,
   *              Spalte `name`) - der Schreibvorgang laeuft ueber `PATCH /api/orders/:id/extras`
   *              und wirkt.
   *   `orderNo`  WIRD ABGEWIESEN. `orderNo` steht in `KEIN_FELDGRUPPEN_WEG` - die Bestellnummer
   *              gehoert JTL (`cEigeneBestellnummer` wird beim Speichern von JTL vergeben und nur
   *              zurueckgelesen, Spec 592-602; `feldinventar.ts`, Klasse "jtl"). Das Tool schickt
   *              sie NIE mit. `gruppeUndFelder` wirft `FeldOhneGruppeFehler`, BEVOR irgendetwas
   *              hinausgeht.
   *
   * FOLGE FUER DEN NUTZER, laut statt still: `DrawerKopf.tsx` zeichnet das Feld "Bestell-Nr." als
   * gewoehnliches, NICHT gesperrtes Eingabefeld und ruft `onFeld("orderNo", …)` bei JEDEM
   * Tastendruck. Jeder einzelne erzeugt damit einen `console.error` plus den Stapel-Eintrag "Die Bestellung
   * konnte nicht geändert werden. …" - waehrend das unmittelbar daneben liegende Feld ganz normal
   * funktioniert. Der Fehlschlag ist LAUT (Konsole + Stapel in `drawerAendern`), nicht verschluckt;
   * der Riegel wird NICHT umgangen. Ein `disabled` am Feld waere eine Abweichung vom Bestand, wo es
   * bedienbar ist - deshalb hier nur benannt, nicht gesperrt.
   *
   * Abgesichert in `test/komponenten/App/verdrahtungDrawer.test.tsx`: dort wird die von hier
   * gesendete Form gegen den ECHTEN `gruppeUndFelder` gehalten (nicht gegen eine Attrappe) - der
   * Erfolgsweg laeuft seither ueber `name`, die Ablehnung ueber `orderNo`, beide im selben `it`.
   */
  function drawerFeldAendern(id: string, feld: string, wert: string): void {
    drawerAendern(id, { [feld]: wert });
  }

  return [drawerAendern, drawerFeldAendern] as const;
}
